// TextInput — thin <input> wrapper with admin-console styling. The
// wrapper does NOT own label/error markup; compose it inside
// <FormField> for that. Keeping the primitive tiny means it can also
// be used standalone (e.g. search boxes) without the label surface.
//
// Touch: renders at 16px (text-base) so iOS Safari does not zoom the
// viewport on focus. Admins on desktop see the same size — the field
// app is the primary target and a uniform size is worth more than a
// denser desktop form.

import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
}

const BASE_CLASSES =
  'block w-full min-h-11 rounded-md border px-3 py-2 text-base text-gray-900 placeholder:text-gray-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)] disabled:cursor-not-allowed disabled:bg-gray-100'

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  function TextInput(
    { invalid = false, className, type = 'text', 'aria-invalid': ariaInvalid, ...rest },
    ref,
  ) {
    const invalidFromAria =
      ariaInvalid !== undefined && ariaInvalid !== false && ariaInvalid !== 'false'
    const showErrorBorder = invalid || invalidFromAria
    const resolvedAriaInvalid =
      ariaInvalid !== undefined ? ariaInvalid : invalid ? true : undefined
    const borderClass = showErrorBorder
      ? 'border-red-500'
      : 'border-gray-300 focus:border-[color:var(--color-brand)]'
    const classes = [BASE_CLASSES, borderClass, className ?? '']
      .filter((c) => c !== '')
      .join(' ')
    return (
      <input
        ref={ref}
        type={type}
        aria-invalid={resolvedAriaInvalid}
        className={classes}
        {...rest}
      />
    )
  },
)
